import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { categories, MAIN_SITE_URL } from '@/mocks/categories';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const navLinks = [
    { label: t('common.nav.home'), to: '/', isHash: false },
    { label: t('common.nav.shoppingGuide'), to: '/guide/usfans-shopping-guide', isHash: false },
    { label: t('common.nav.categories'), to: '/#categories', isHash: true },
    { label: t('common.nav.spreadsheet'), to: '/usfans-spreadsheet-guide', isHash: false },
    { label: t('common.nav.faq'), to: '/#faq', isHash: true },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background-50 border-b border-background-300/70">
      <div className="flex items-center justify-between h-16 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2.5 group shrink-0">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 group-hover:bg-primary-600 transition-colors">
            <i className="ri-table-line text-lg"></i>
          </span>
          <span className="font-heading font-bold text-base md:text-lg text-foreground-950 whitespace-nowrap">
            {t('common.brand')}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-0.5">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className="px-3.5 py-2 rounded-md text-sm font-medium text-foreground-800 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <div className="relative" ref={langRef}>
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors whitespace-nowrap"
              aria-label={t('common.languageLabel')}
            >
              <i className="ri-global-line text-base"></i>
              <span className="hidden sm:inline">{currentLang.label}</span>
              <span className="sm:hidden uppercase">{currentLang.code}</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 rounded-lg border border-background-200 bg-background-50 py-1">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full flex items-center justify-between px-4 py-2 text-sm text-left transition-colors hover:bg-background-100 ${
                      lang.code === i18n.language ? 'text-primary-600 font-semibold' : 'text-foreground-700'
                    }`}
                  >
                    <span>{lang.label}</span>
                    {lang.code === i18n.language && <i className="ri-check-line"></i>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            {t('common.cta.explore')}
          </a>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 transition-colors"
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors flex items-center justify-between"
              >
                <span>{link.label}</span>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </Link>
            ))}
          </div>

          <div className="px-4 pb-3">
            <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t('common.categoriesTitle')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-700 bg-background-100 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded bg-secondary-100 text-secondary-700">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  <span className="truncate">{t(`categories.${cat.key}`)}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="px-4 pb-4">
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-1.5 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
            >
              {t('common.cta.explore')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}